/**
 * OpenLayers 10.x Bridge Module
 * 
 * Provides a compatibility layer matching ol3-bridge.ts exports
 * while using OpenLayers 10 ES6 modules underneath.
 * 
 * This allows switching from OL3 to OL10 by changing one import:
 *   Before: import { Map } from './openlayers/ol3-bridge';
 *   After:  import { Map } from './openlayers/ol10-bridge';
 * 
 * Note: CSS is imported in ol3-bridge.ts for OL3 (from lib/ol/ol.css)
 * For OL10, CSS should be imported in the application entry point or webpack config
 * to avoid loading both CSS versions when ol-bridge imports both bridges.
 */

// =============================================================================
// Core Classes (Direct Exports)
// =============================================================================
import OLMap from 'ol/Map';
import OLView from 'ol/View';
import OLFeature from 'ol/Feature';
import OLOverlay from 'ol/Overlay';
import OLCollection from 'ol/Collection';

export const Map = OLMap;
export const View = OLView;
export const Feature = OLFeature;
export const Overlay = OLOverlay;
export const Collection = OLCollection;

// =============================================================================
// Layers
// =============================================================================
import OLTileLayer from 'ol/layer/Tile';
import OLVectorLayer from 'ol/layer/Vector';
import OLLayerGroup from 'ol/layer/Group';
import OLBaseLayer from 'ol/layer/Base';

export const TileLayer = OLTileLayer;
export const VectorLayer = OLVectorLayer;

// Namespace export for compatibility with: new ol.layer.Tile()
export const layer = {
    Tile: OLTileLayer,
    Vector: OLVectorLayer,
    Group: OLLayerGroup,
    Base: OLBaseLayer,
};

// =============================================================================
// Sources
// =============================================================================
import OLOSM from 'ol/source/OSM';
import OLXYZ from 'ol/source/XYZ';
import OLVectorSource from 'ol/source/Vector';
import OLCluster from 'ol/source/Cluster';
import OLTileGrid from 'ol/tilegrid/TileGrid';

export const XYZ = OLXYZ;
export const VectorSource = OLVectorSource;
export const ClusterSource = OLCluster;
export const TileGrid = OLTileGrid;

export const source = {
    OSM: OLOSM,
    XYZ: OLXYZ,
    Vector: OLVectorSource,
    Cluster: OLCluster,
};

export const tilegrid = {
    TileGrid: OLTileGrid,
};

// =============================================================================
// Geometry
// =============================================================================
import OLPoint from 'ol/geom/Point';
import OLPolygon from 'ol/geom/Polygon';
import OLLineString from 'ol/geom/LineString';
import OLCircleGeom from 'ol/geom/Circle';

// Note: GeomPoint alias to avoid conflict with map feature Point class
export const GeomPoint = OLPoint;
export const Polygon = OLPolygon;
export const LineString = OLLineString;

export const geom = {
    Point: OLPoint,
    Polygon: OLPolygon,
    LineString: OLLineString,
    Circle: OLCircleGeom,
};

// =============================================================================
// Interactions
// =============================================================================
import { defaults as defaultInteractions } from 'ol/interaction';
import OLDraw from 'ol/interaction/Draw';
import OLModify from 'ol/interaction/Modify';
import OLSelect from 'ol/interaction/Select';

export const Modify = OLModify;

export const interaction = {
    defaults: defaultInteractions,
    Draw: OLDraw,
    Modify: OLModify,
    Select: OLSelect,
};

// =============================================================================
// Controls
// =============================================================================
import { defaults as defaultControls } from 'ol/control';
import OLControl from 'ol/control/Control';
import OLMousePosition from 'ol/control/MousePosition';

export const Control = OLControl;
export const MousePosition = OLMousePosition;

export const control = {
    defaults: defaultControls,
    Control: OLControl,
    MousePosition: OLMousePosition,
};

// =============================================================================
// Projections & Coordinate Utilities
// =============================================================================
import { transform as olTransform, transformExtent } from 'ol/proj';
import OLProjection from 'ol/proj/Projection';
import { createStringXY as olCreateStringXY } from 'ol/coordinate';
import { getCenter as olGetCenter } from 'ol/extent';

export const Projection = OLProjection;

export const proj = {
    transform: olTransform,
    transformExtent: transformExtent,
    Projection: OLProjection,
};

export const coordinate = {
    createStringXY: olCreateStringXY,
};

export const extent = {
    getCenter: olGetCenter,
};

// =============================================================================
// Styles
// =============================================================================
import OLStyle from 'ol/style/Style';
import OLIcon from 'ol/style/Icon';
import OLFill from 'ol/style/Fill';
import OLStroke from 'ol/style/Stroke';
import OLText from 'ol/style/Text';
import OLCircleStyle from 'ol/style/Circle';

export const Style = OLStyle;
export const Icon = OLIcon;
export const Fill = OLFill;
export const Stroke = OLStroke;
export const Text = OLText;
export const Circle = OLCircleStyle;

export const style = {
    Style: OLStyle,
    Icon: OLIcon,
    Fill: OLFill,
    Stroke: OLStroke,
    Text: OLText,
    Circle: OLCircleStyle,
};

// =============================================================================
// Formats
// =============================================================================
import OLGeoJSON from 'ol/format/GeoJSON';

export const GeoJSON = OLGeoJSON;

export const format = {
    GeoJSON: OLGeoJSON,
};

// =============================================================================
// Custom Control Inheritance (OL3 → OL10 Breaking Change)
// =============================================================================

/**
 * OL3 used ol.inherits() for prototype-based inheritance.
 * OL10 requires ES6 class syntax. This helper maintains compatibility
 * for legacy code during migration.
 * 
 * DEPRECATED: Migrate to ES6 classes instead.
 * 
 * @example
 * // Old (OL3)
 * function CustomControl(opt_options) {
 *   ol.control.Control.call(this, { element: el });
 * }
 * ol.inherits(CustomControl, ol.control.Control);
 * 
 * // New (OL10)
 * class CustomControl extends Control {
 *   constructor(options) {
 *     super({ element: el, ...options });
 *   }
 * }
 */
export function inherits(childCtor: any, parentCtor: any): void {
    console.warn(
        '[ol10-bridge] inherits() is deprecated. Use ES6 class syntax: ' +
        'class CustomControl extends Control { ... }'
    );
    // Simple prototype chain setup for legacy compatibility
    childCtor.prototype = Object.create(parentCtor.prototype);
    childCtor.prototype.constructor = childCtor;
}

// =============================================================================
// Namespace Export (for gradual migration)
// =============================================================================

/**
 * Full namespace export for code that hasn't been refactored yet.
 * 
 * Usage: import { olNamespace as ol } from './ol10-bridge';
 *        new ol.Map({ ... });
 * 
 * DEPRECATED: Use named imports instead for tree-shaking benefits.
 */
export const olNamespace = {
    Map: OLMap,
    View: OLView,
    Feature: OLFeature,
    Overlay: OLOverlay,
    Collection: OLCollection,
    layer,
    source,
    geom,
    interaction,
    control,
    proj,
    coordinate,
    extent,
    style,
    format,
    inherits,
};